import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';

import { PlatformLocale, useGetOrganizationMessages, useGetProcessMessages } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';

import MessageCard from './MessageCard';
import MessageViewModal from './MessageViewModal';

interface SystemMessage {
  langId?: string;
  msgType?: string;
  msgTitle?: string;
  msgContent?: string;
}

enum MessageCategory {
  ERROR = 'error',
  TRANSACTION = 'transaction',
  WELCOME = 'welcome',
  REPORT = 'report',
  CONGRATULATIONS = 'congratulations',
  INVITATION = 'invitation'
}

enum MessageType {
  INVITATION = 'invitation',
  ANONYMOUS_INVITATION_LINK = 'anonymousInvitationLink',
  ERROR = 'error',
  CAMERA_TERMS = 'cameraTerms',
  COMPLETE = 'complete',
  TALENT_REPORT_SENT = 'talentReportSent',
  PROCESSING_TERMS = 'processingTerms',
  RESEARCH_TERMS = 'researchTerms',
  WELCOME = 'welcome'
}

const IconWrapper = ({ src }: { src: string }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    component="img"
    src={src}
  />
);

const TransactionIcon = () => <IconWrapper src="/images/invitation.svg" />;

const WelcomeIcon = () => <IconWrapper src="/images/hello.svg" />;

const ReportIcon = () => <IconWrapper src="/images/list.svg" />;

const InvitationIcon = () => <IconWrapper src="/images/invitation.svg" />;

const CongratulationsIcon = () => <IconWrapper src="/images/congratulations-check.svg" />;
const ErrorIcon = () => <IconWrapper src="/images/error.svg" />;

const getCategoryForType = (type: string): MessageCategory | undefined => {
  switch (type) {
    case MessageType.ERROR:
      return MessageCategory.ERROR;
    case MessageType.ANONYMOUS_INVITATION_LINK:
    case MessageType.INVITATION:
      return MessageCategory.INVITATION;
    case MessageType.COMPLETE:
    case MessageType.TALENT_REPORT_SENT:
      return MessageCategory.CONGRATULATIONS;

    case MessageType.WELCOME:
      return MessageCategory.WELCOME;

    case MessageType.CAMERA_TERMS:
    case MessageType.PROCESSING_TERMS:
    case MessageType.RESEARCH_TERMS:
      return MessageCategory.REPORT;

    default:
      return undefined;
  }
};

const getIconForType = (type: string) => {
  const category = getCategoryForType(type);

  switch (category) {
    case MessageCategory.TRANSACTION:
      return <TransactionIcon />;
    case MessageCategory.WELCOME:
      return <WelcomeIcon />;
    case MessageCategory.REPORT:
      return <ReportIcon />;
    case MessageCategory.CONGRATULATIONS:
      return <CongratulationsIcon />;
    case MessageCategory.INVITATION:
      return <InvitationIcon />;
    case MessageCategory.ERROR:
      return <ErrorIcon />;
    default:
      return <ReportIcon />;
  }
};

interface MessagesTabProps {
  organizationSlug?: string;
  processId?: string;
}

const MessagesTab: FC<MessagesTabProps> = ({ organizationSlug, processId }) => {
  const { t, i18n } = useTranslation(['common', 'processes']);
  const [selectedMessage, setSelectedMessage] = useState<SystemMessage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: { messages: processMessages } = {},
    isLoading: isProcessMessagesLoading,
    error: processMessagesError
  } = useGetProcessMessages(
    processId!,
    { locale: i18n.language as PlatformLocale },
    {
      query: {
        enabled: !!processId
      }
    }
  );

  const {
    data: { messages: organizationMessages } = {},
    isLoading: isOrganizationMessagesLoading,
    error: organizationMessagesError
  } = useGetOrganizationMessages(
    organizationSlug!,
    { locale: i18n.language as PlatformLocale },
    {
      query: {
        enabled: !!organizationSlug
      }
    }
  );

  const messages = Object.entries(processMessages || organizationMessages || {}).map(([msgType, msgContent]) => ({
    langId: i18n.language,
    msgType,
    msgContent,
    msgTitle: t(`processes:layouts.process.messages.${msgType}`, { defaultValue: msgType })
  }));

  const isLoading = isProcessMessagesLoading || isOrganizationMessagesLoading;

  const error = processMessagesError || organizationMessagesError;

  const handleMessageClick = useCallback((message: SystemMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  }, []);

  if (isLoading) {
    return <BackdropLoader />;
  }

  if (error || !messages) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}
      >
        {t('common:error.somethingWentWrong')}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100%'
      }}
    >
      <Grid container spacing={3}>
        {messages?.map((message: SystemMessage) => (
          <Grid item xs={12} md={4} key={message.msgType}>
            <MessageCard
              icon={getIconForType(message.msgType!)}
              title={message.msgTitle!}
              content={message.msgContent!}
              onClick={() => handleMessageClick(message)}
            />
          </Grid>
        ))}
      </Grid>

      {selectedMessage && (
        <MessageViewModal
          open={isModalOpen}
          onClose={handleCloseModal}
          msgType={selectedMessage.msgType!}
          icon={getIconForType(selectedMessage.msgType!)}
          organizationSlug={organizationSlug}
          processId={processId}
        />
      )}
    </Box>
  );
};

export default MessagesTab;
